import { QueryClient } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import { basenameForLocale, parsePath } from "./lib/i18n/locales";

// The Classic app mounted the whole router under a per-locale basename
// (/es, /fr, … — English at the root). We preserve that with TanStack's
// `basepath`: on the client it is derived from the current URL; on the server
// src/server.ts stashes the request's basepath in an AsyncLocalStorage exposed
// on globalThis (so this shared module never imports node:async_hooks).
function detectBasepath(): string {
  if (typeof window !== "undefined") {
    return basenameForLocale(parsePath(window.location.pathname).locale) || "/";
  }
  const store = (
    globalThis as { __statlignLocaleBasepath?: { getStore: () => string | undefined } }
  ).__statlignLocaleBasepath;
  return store?.getStore() || "/";
}

export const getRouter = () => {
  // Tuned for read-heavy public traffic at scale: cache list/query results for
  // a short window and avoid refetch storms (ported from the Classic App.tsx).
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000, // 1 min: treat data as fresh, dedupe rapid refetches
        gcTime: 5 * 60 * 1000, // keep unused data 5 min for instant back-nav
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
  });

  const basepath = detectBasepath();

  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
    // Start loading a page as soon as the pointer/finger lands on its link, and
    // show the loading bar quickly so a click never looks ignored.
    defaultPreload: "intent",
    defaultPendingMs: 150,
    defaultPendingMinMs: 0,
    basepath,
  });

  // TanStack Start's request handler calls `router.update({ ..., basepath:
  // process.env.TSS_ROUTER_BASEPATH || '/' })` on every request, which wiped our
  // per-locale basepath and made every localized URL (/fr, /es/faq, …) render the
  // 404 route. Statlign serves one locale per URL prefix, so the basepath is
  // per-request and cannot come from a build-time env var: pin it here so
  // framework updates can never override it.
  const update = router.update.bind(router);
  router.update = ((options: Parameters<typeof update>[0]) =>
    update({ ...options, basepath })) as typeof router.update;

  return router;
};

